// src/modules/activity/pages/ActivityPage/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import { Container, Alert, Button, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import RegisterActivityForm from '../../components/RegisterActivityForm';
import ActivityView from '../../components/ActivityView';
import Loading from '../../../../shared/components/Loading';
import { useAlert } from '../../../../shared/hooks/useAlert';
import { useService } from '../../../../shared/hooks/useService';
import { useAuth } from '../../../../shared/hooks/useAuth';

const initialFormState = {
    type: '',
    distance: 0,
    calories: 0,
    elevation: 0,
    duration: '00:00:00',
    date: new Date(),
    attachments: []
};

const ErrorState = ({ message, onRetry, onBack }) => (
    <Container className="text-center mt-5">
        <Alert variant="danger">
            <Alert.Heading>Error Loading Activity</Alert.Heading>
            <p>{message}</p>
        </Alert>
        <div className="mt-3">
            <Button variant="primary" onClick={onRetry} className="me-2">
                Try Again
            </Button>
            <Button variant="secondary" onClick={onBack}>
                Go Back
            </Button>
        </div>
    </Container>
);

const RemoveAttachmentModal = ({ show, onHide, onConfirm, attachmentToRemove }) => (
    <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
            <Modal.Title>Remove Attachment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {attachmentToRemove !== null ? (
                <p>Are you sure you want to remove {attachmentToRemove.name}?</p>
            ) : (
                <p>Invalid attachment</p>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
                Cancel
            </Button>
            {attachmentToRemove !== null && (
                <Button variant="danger" onClick={onConfirm}>
                    Delete attachment
                </Button>
            )}
        </Modal.Footer>
    </Modal>
);

const ActivityPage = () => {
    const [activity, setActivity] = useState(null);
    const [activityData, setActivityData] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [error, setError] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [showRemoveAttachmentModal, setShowRemoveAttachmentModal] = useState(false);
    const [attachmentToRemove, setAttachmentToRemove] = useState(null);
    // AI image analysis state
    // eslint-disable-next-line no-unused-vars
    const [aiSuggestions, setAiSuggestions] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [autoFilledFields, setAutoFilledFields] = useState(new Set());
    const [aiSuggestionsEnabled, setAiSuggestionsEnabled] = useState(() => {
        const saved = localStorage.getItem('fitops-ai-suggestions-enabled');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const handleToggleAiSuggestions = useCallback((enabled) => {
        setAiSuggestionsEnabled(enabled);
        localStorage.setItem('fitops-ai-suggestions-enabled', JSON.stringify(enabled));
    }, []);

    const { id } = useParams();
    const { alert, showAlert, showSuccess, showError, hideAlert } = useAlert();
    const service = useService();
    const navigate = useNavigate();
    const { user, hasPermission } = useAuth();

    const isOwner = user?.Username === activity?.username;
    const hasHandleExemptionsPermission = hasPermission('activities:handleExemptions');

    const loadActivity = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await service.fetchActivity(id);
            if (!data) {
                throw new Error('Activity not found');
            }
            setActivity(data);
            console.log("data: ", data)

            // Convert activity data to form format
            const totalSeconds = data.duration;
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            const durationString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

            setActivityData({
                type: data.activityType,
                distance: data.distance.toString(),
                calories: data.calories,
                elevation: data.elevation.toString(),
                duration: durationString,
                date: new Date(data.date),
                attachments: data.attachments || []
            });
            setAttachments(data.attachments || [])
        } catch (error) {
            setError(error.message);
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [id, service, showError]); // Add dependencies

    useEffect(() => {
        if (id) {
            loadActivity();
        } else {
            // Reset form state when creating new activity after editing
            setActivityData(initialFormState);
            setAttachments([]);
        }
    }, [id, loadActivity]);

    // Combine handleChange and handleEditChange since they're identical
    const handleChange = useCallback((e) => {
        const { name, value, files } = e.target;
        if (name === 'attachments' && files) {
            setActivityData(prev => ({
                ...prev,
                attachments: Array.from(files)
            }));
        } else {
            setActivityData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    }, []);

    const handleRemoveAttachment = useCallback((attachment) => {
        setAttachmentToRemove(attachment);
        setShowRemoveAttachmentModal(true);
    }, []);

    const handleConfirmRemoveAttachment = useCallback(() => {
        if (attachmentToRemove) {
            setAttachments((prevAttachments) => prevAttachments.filter(attachment => attachment.name !== attachmentToRemove.name));
            setActivityData((prevData) => ({
                ...prevData,
                attachments: (prevData.attachments || []).filter((attachment) => attachment.name !== attachmentToRemove.name),
            }));
        }
        setShowRemoveAttachmentModal(false);
        setAttachmentToRemove(null);
    }, [attachmentToRemove]);

    const handleCancelRemoveAttachment = useCallback(() => {
        setShowRemoveAttachmentModal(false);
        setAttachmentToRemove(null);
    }, []);

    // AI image analysis handlers
    const applyAiSuggestions = useCallback((suggestions) => {
        const MIN_CONFIDENCE = 0.70;
        const fieldsToAutoFill = new Set();
        const updates = {};

        if (suggestions.activityType?.value && suggestions.activityType.confidence >= MIN_CONFIDENCE) {
            updates.type = suggestions.activityType.value;
            fieldsToAutoFill.add('type');
        }

        if (suggestions.distance?.value && suggestions.distance.confidence >= MIN_CONFIDENCE) {
            updates.distance = suggestions.distance.value.toString();
            fieldsToAutoFill.add('distance');
        }

        if (suggestions.duration?.value && suggestions.duration.confidence >= MIN_CONFIDENCE) {
            const seconds = suggestions.duration.value;
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            updates.duration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
            fieldsToAutoFill.add('duration');
        }

        if (suggestions.elevation?.value && suggestions.elevation.confidence >= MIN_CONFIDENCE) {
            updates.elevation = suggestions.elevation.value.toString();
            fieldsToAutoFill.add('elevation');
        }

        if (suggestions.calories?.value && suggestions.calories.confidence >= MIN_CONFIDENCE) {
            updates.calories = suggestions.calories.value.toString();
            fieldsToAutoFill.add('calories');
        }

        setActivityData(prev => ({ ...prev, ...updates }));
        setAutoFilledFields(fieldsToAutoFill);

        // Clear highlighting after 10 seconds
        setTimeout(() => setAutoFilledFields(new Set()), 10000);
    }, []);

    const handleImageAnalysis = useCallback(async (imageFile, fileName) => {
        setIsAnalyzing(true);

        try {
            const result = await service.analyzeActivityImage(
                imageFile,
                fileName,
                activityData.type || null
            );

            if (result.success && result.suggestions) {
                setAiSuggestions(result.suggestions);
                applyAiSuggestions(result.suggestions);
                showSuccess('AI analysis complete! Review the suggested values.');
            } else {
                console.warn('Image analysis failed:', result.message);
                showAlert({
                    variant: 'warning',
                    message: result.message || 'Could not analyze image. Please enter values manually.'
                });
            }
        } catch (error) {
            console.error('Image analysis error:', error);
        } finally {
            setIsAnalyzing(false);
        }
    }, [service, activityData.type, applyAiSuggestions, showSuccess, showAlert]);

    const handleFileChange = useCallback(async (e) => {
        const newFiles = Array.from(e.target.files).map(file => ({ file, name: file.name, type: file.type }));
        const imageFiles = newFiles.filter(f => f.type.startsWith('image/'));

        setAttachments([...attachments, ...newFiles]);
        setActivityData(prev => prev ? {
            ...prev,
        } : { ...initialFormState });

        // Trigger AI analysis for the first image (only for new activities and if enabled)
        if (imageFiles.length > 0 && !id && aiSuggestionsEnabled) {
            const firstImage = imageFiles[0];
            // Trigger analysis asynchronously
            handleImageAnalysis(firstImage.file, firstImage.name);
        }
    }, [attachments, id, handleImageAnalysis, aiSuggestionsEnabled]);

    const handleDelete = useCallback(async () => {
        setIsLoading(true);
        try {
            await service.deleteActivity(id);
            showSuccess('Activity deleted successfully');
            navigate('/activities');
        } catch (error) {
            setError(error.message);
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [id, service, showSuccess, showError, navigate]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            if (!activityData.type) {
                throw new Error('Please select an activity type');
            }
            if (activityData.type !== 'other' && (!activityData.distance || parseFloat(activityData.distance) <= 0)) {
                throw new Error('Please enter a valid distance');
            }

            // Validate and convert duration from HH:MM:SS to seconds
            const durationParts = activityData.duration.split(':');
            const hours = parseInt(durationParts[0]) || 0;
            const minutes = parseInt(durationParts[1]) || 0;
            const seconds = parseInt(durationParts[2]) || 0;
            const totalDuration = hours * 3600 + minutes * 60 + seconds;

            if (totalDuration <= 0) {
                throw new Error('Duration must be greater than 0');
            }

            const formattedData = {
                type: activityData.type,
                distance: parseFloat(activityData.distance),
                calories: parseFloat(activityData.calories),
                elevation: parseFloat(activityData.elevation),
                duration: totalDuration,
                date: activityData.date.toISOString(),
                attachments
            };

            if (id) {
                await service.updateActivity(id, formattedData);
                loadActivity()
                showSuccess('Activity updated successfully');
            } else {
                await service.createActivity(formattedData);
                showSuccess('Activity registered successfully');
                navigate('/activities');
            }
        } catch (error) {
            setError(error.message);
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, activityData, attachments, service, showSuccess, showError, navigate, loadActivity]);

    const handleRetry = useCallback(() => {
        if (id) {
            loadActivity();
        } else {
            setAttachments([])
            setError(null);
        }
    }, [id, loadActivity]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleRequestExemption = useCallback(async (note) => {
        setIsLoading(true);
        try {
            await service.requestActivityExemption(id, note);
            showSuccess('Exemption request submitted');
            await loadActivity();
        } catch (error) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [id, service, showSuccess, showError, loadActivity]);

    const handleReviewExemption = useCallback(async (action, note) => {
        setIsLoading(true);
        try {
            await service.reviewActivityExemption(id, action, note);
            showSuccess(`Exemption ${action}ed`);
            await loadActivity();
        } catch (error) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [id, service, showSuccess, showError, loadActivity]);

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (error) {
        return (
            <ErrorState
                message={error}
                onRetry={handleRetry}
                onBack={handleBack}
            />
        );
    }

    return (
        <Container className="mt-3">
            {id ? (
                activity ? (
                    <ActivityView
                        activity={activity}
                        attachments={attachments}
                        activityData={activityData}
                        onEdit={handleSubmit}
                        onDelete={handleDelete}
                        handleChange={handleChange}
                        handleFileChange={handleFileChange}
                        handleRemoveAttachment={handleRemoveAttachment}
                        alert={alert}
                        showAlert={showAlert}
                        onAlertClose={hideAlert}
                        isLoading={isLoading}
                        isOwner={isOwner}
                        hasHandleExemptionsPermission={hasHandleExemptionsPermission}
                        onRequestExemption={handleRequestExemption}
                        onReviewExemption={handleReviewExemption}
                    />
                ) : (
                    <Loading isWarmup={isWarmup} />
                )
            ) : (
                <RegisterActivityForm
                    key={id || 'create'}
                    activityData={activityData}
                    attachments={attachments}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    handleFileChange={handleFileChange}
                    handleRemoveAttachment={handleRemoveAttachment}
                    isLoading={isLoading}
                    alert={alert}
                    showAlert={showAlert}
                    onAlertClose={hideAlert}
                    autoFilledFields={autoFilledFields}
                    isAnalyzing={isAnalyzing}
                    aiSuggestionsEnabled={aiSuggestionsEnabled}
                    onToggleAiSuggestions={handleToggleAiSuggestions}
                />
            )}
            <RemoveAttachmentModal
                show={showRemoveAttachmentModal}
                onHide={handleCancelRemoveAttachment}
                onConfirm={handleConfirmRemoveAttachment}
                attachmentToRemove={attachmentToRemove}
            />
        </Container>
    );
};

export default ActivityPage;
